import { useContext, useCallback, useState } from 'react';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { useBrandKitDataService } from '@bingads-webui-campaign-react/brandkit-data-service';
import { setClarityTag } from '@bingads-webui-universal/clarity';

import { processAndGroupAds, errors } from '../model/constants';

const formatReturnedSuggestions = suggestions => ({
  RecommendedAssets: {
    ImageAssets: suggestions.map(({ recommendationId }) =>
      ({ RecommendationId: recommendationId })),
  },
});

const formatBrandkitForAPI = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(formatBrandkitForAPI);
  } else if (obj !== null && typeof obj === 'object') {
    const formattedObj = {};
    Object.keys(obj).forEach((key) => {
      if (key === 'Id') {
        formattedObj.Id = parseInt(obj[key], 10);
      } else if (key === 'SubType') {
        formattedObj.SubType = 'None';
      } else if (key !== 'Type') {
        formattedObj[key] = formatBrandkitForAPI(obj[key]);
      }
    });
    return formattedObj;
  }
  return obj;
};

export const useDisplayAdsCopilot = () => {
  const [, setRecommendationContext] = useContext(RecommendationContext);
  const { logBannerGenerated } = useCopilotLoggers();
  const [isLoadingBrandKit, setIsLoadingBrandkit] = useState(false);
  const [brandkit, setBrandKit] = useState(null);
  const [isBrandKitEmpty, setIsBrandKitEmpty] = useState(true);
  const { scope } = useContext(ScopeContext) || {};

  const { aid } = scope.values;
  const permissions = useContext(PermissionsContext);
  const serverContext = useContext(ServerContext);
  const isBrandKitEnabled = permissions && permissions.dynamic && permissions.dynamic.BrandKitUI;

  const repository = useCopilotRepositoryContext();

  const {
    fetchDisplayAdsAssetRecommendations,
    fetchDisplayAdsTemplateDefinition,
    editDisplayAdsAssetRecommendations,
    finalUrl,
  } = repository;

  const { getBrandKitDataByAccId } = useBrandKitDataService();

  const fetchBrandKit = useCallback(async () => {
    if (isBrandKitEnabled) {
      const restrictionCall = isCopilotRegionRestricted({
        feature: 'Recommendations',
        permissions,
        serverContext,
      });
      setIsLoadingBrandkit(true);
      const response = await getBrandKitDataByAccId({ accId: aid });
      const isRestricted = await restrictionCall;
      if (!isRestricted) {
        const isBrandKitHavingData = response?.BrandKits?.[0] && Object.keys(response.BrandKits[0]).length > 0 && (response.BrandKits[0].Fonts?.length !== 0 ||
          response.BrandKits[0].Images?.length !== 0 ||
          response.BrandKits[0].LandscapeLogos?.length !== 0 ||
          response.BrandKits[0].Palettes?.length !== 0 ||
          response.BrandKits[0].SquareLogos?.length !== 0);
        if (isBrandKitHavingData) {
          setIsBrandKitEmpty(false);
          setClarityTag({ DisplayAdsWithBrandKit: 'true' });
          setBrandKit(formatBrandkitForAPI(response.BrandKits[0]));
        } else {
          setIsBrandKitEmpty(true);
        }
        setIsLoadingBrandkit(false);
      } else {
        setIsBrandKitEmpty(true);
        setIsLoadingBrandkit(false);
      }
    }
  }, [isBrandKitEnabled, getBrandKitDataByAccId, aid, serverContext, permissions, setBrandKit, setIsBrandKitEmpty]);

  const fetch = useCallback(async (count = 10, useBrandKit) => {
    if (!finalUrl) {
      return;
    }

    setRecommendationContext({
      isLoading: true,
    });

    if (!isLoadingBrandKit) {
      let templateVersion = '1.1';
      if (isBrandKitEnabled) {
        templateVersion = '1.3';
      }

      fetchDisplayAdsAssetRecommendations(count, (useBrandKit && brandkit) ? brandkit : null, templateVersion).then((response) => {
        const { ImageAssets, Ads } = response;

        const {
          groupedAds,
          imagesToSuggest,
        } = processAndGroupAds(Ads);

        setRecommendationContext({
          ImageAssets,
          Ads,
          isLoading: false,
          finalUrl,
          groupedAds,
          imagesToSuggest,
          errorMessage: null,
        });

        logBannerGenerated(finalUrl, formatReturnedSuggestions(Ads));
      }).catch((e) => {
        if (e.message === 'NO_RECOMMENDATION') {
          setRecommendationContext({
            isLoading: false,
            finalUrl,
            imagesToSuggest: [],
            Ads: [],
            groupedAds: [],
            errorMessage: errors.NO_RECOMMENDATION,
          });
        }

        setRecommendationContext({
          isLoading: false,
          finalUrl,
          imagesToSuggest: [],
          Ads: [],
          groupedAds: [],
          errorMessage: (e && e.errors && e.errors[0] && errors[e.errors[0].target]) || errors.DEFAULT,
        });
      });
    }
  }, [finalUrl, setRecommendationContext, isLoadingBrandKit, isBrandKitEnabled, fetchDisplayAdsAssetRecommendations, brandkit, logBannerGenerated]);

  const fetchAigcTemplateDefinition = useCallback(templateId => fetchDisplayAdsTemplateDefinition(templateId)
    .then(response => response)
    .catch(err => ({ err })), [fetchDisplayAdsTemplateDefinition]);


  const generate = useCallback(ads => editDisplayAdsAssetRecommendations(ads).then((updatedCreative) => {
    const { Ads } = updatedCreative;

    return Ads && Ads[0];
  }), [editDisplayAdsAssetRecommendations]);

  return {
    fetch,
    fetchAigcTemplateDefinition,
    generate,
    finalUrlForCopilot: finalUrl,
    isBrandKitEmpty,
    fetchBrandKit,
  };
};
